import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Form, ButtonGroup, Dropdown, Modal, Accordion, Spinner } from 'react-bootstrap';
import { fabric } from 'fabric';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage, 
  faFont, 
  faUndo, 
  faRedo,
  faTrash,
  faShoppingCart,
  faImages,
  faEdit,
  faArrowsAltH,
  faArrowsAltV,
  faRotateLeft,
  faRotateRight,
  faEye,
  faEyeSlash,
  faLayerGroup,
  faArrowUp,
  faArrowDown,
  faCheck,
  faInfoCircle,
  faShoppingBag,
  faTimes,
  faMagic,
  faFilter
} from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../context/CartContext';
import { toast } from 'react-toastify';
import api from '../config/api';
import parameterService from '../services/parameterService';
import './CustomDesign.css';
import { fetchPhoneModelTemplates } from '../services/phoneTemplateService';
import { useLocation, useNavigate } from 'react-router-dom';
import AIImageGenerator from '../components/AIImageGenerator';
import PhotoFilters from '../components/PhotoFilters';

const CustomDesign = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const [canvas, setCanvas] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [maxHistorySteps] = useState(50); // Maksimum geçmiş adımı
  const [phoneModels, setPhoneModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedModelColors, setSelectedModelColors] = useState([]);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedColorName, setSelectedColorName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(''); // Yükleme durumu için yeni state
  const { addToCart, cart } = useCart();
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const { user } = useCart();
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [isLoadingDesigns, setIsLoadingDesigns] = useState(false);
  const [objectFilters, setObjectFilters] = useState({});
  const [showEditTools, setShowEditTools] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const [layers, setLayers] = useState([]);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [showColorInfo, setShowColorInfo] = useState(false);
  const [showApprovalInfo, setShowApprovalInfo] = useState(false);
  const [isApprovalChecked, setIsApprovalChecked] = useState(false);
  // Stok bilgilerini tutacak state
  const [stockInfo, setStockInfo] = useState({});
  const [currentColorStockInfo, setCurrentColorStockInfo] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [cartItemsCount, setCartItemsCount] = useState({});
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [showPhotoFilters, setShowPhotoFilters] = useState(false);
  const [kendinTasarlaFee, setKendinTasarlaFee] = useState(null);

  // Stok durumuna göre en uygun model ve renk seçimi
  const selectBestAvailableModelAndColor = async (phoneModels) => {
    try {
      // Sepetteki mevcut ürünleri al (stok hesaplaması için)
      const currentCart = cart || [];
      
      // 1. Öncelik: iPhone 11 modeli
      const iphone11 = phoneModels.find(model => 
        model.brand === 'iPhone' && model.model_name === '11'
      );
      
      if (iphone11 && iphone11.colors && iphone11.colors.length > 0) {
        // iPhone 11 bulundu, siyah rengi ara (id: 1)
        const blackColor = iphone11.colors.find(color => color.id === 1);
        
        if (blackColor && blackColor.stock > 0) {
          // Sepetteki bu model+renk kombinasyonunu kontrol et
          const cartItemsWithSameModelAndColor = currentCart.filter(item => 
            item.phone_model_id === iphone11.id && 
            item.color_id === blackColor.id
          );
          const cartQuantity = cartItemsWithSameModelAndColor.reduce((sum, item) => sum + item.quantity, 0);
          const remainingStock = Math.max(0, blackColor.stock - cartQuantity);
          
          if (remainingStock > 0) {
            console.log('iPhone 11 Siyah seçildi - Stok:', remainingStock);
            return {
              model: iphone11,
              color: blackColor
            };
          }
        }
        
        // Siyah stokta yoksa iPhone 11'in diğer renklerini kontrol et
        for (const color of iphone11.colors) {
          if (color.id !== 1 && color.stock > 0) { // Siyah olmayan renkler
            const cartItemsWithSameModelAndColor = currentCart.filter(item => 
              item.phone_model_id === iphone11.id && 
              item.color_id === color.id
            );
            const cartQuantity = cartItemsWithSameModelAndColor.reduce((sum, item) => sum + item.quantity, 0);
            const remainingStock = Math.max(0, color.stock - cartQuantity);
            
            if (remainingStock > 0) {
              console.log(`iPhone 11 ${color.name} seçildi - Stok:`, remainingStock);
              return {
                model: iphone11,
                color: color
              };
            }
          }
        }
      }
      
      // 2. iPhone 11'de stok yoksa diğer modelleri kontrol et
      for (const model of phoneModels) {
        // iPhone 11'i atla (yukarıda kontrol edildi)
        if (model.brand === 'iPhone' && model.model_name === '11') {
          continue;
        }
        
        if (model.colors && model.colors.length > 0) {
          // Bu modelin stokta olan ilk rengini bul
          for (const color of model.colors) {
            if (color.stock > 0) {
              const cartItemsWithSameModelAndColor = currentCart.filter(item => 
                item.phone_model_id === model.id && 
                item.color_id === color.id
              );
              const cartQuantity = cartItemsWithSameModelAndColor.reduce((sum, item) => sum + item.quantity, 0);
              const remainingStock = Math.max(0, color.stock - cartQuantity);
              
              if (remainingStock > 0) {
                console.log(`${model.brand} ${model.model_name} ${color.name} seçildi - Stok:`, remainingStock);
                return {
                  model: model,
                  color: color
                };
              }
            }
          }
        }
      }
      
      // Hiçbir model+renk kombinasyonunda stok bulunamadı
      console.warn('Hiçbir model+renk kombinasyonunda stok bulunamadı');
      return null;
      
    } catch (error) {
      console.error('Model ve renk seçimi sırasında hata:', error);
      return null;
    }
  };

  // loadPhoneTemplate fonksiyonunu güncelle
  const loadPhoneTemplate = async (templatePath, maskPath) => {
    if (!canvas) {
      console.error('Canvas bulunamadı');
      return;
    }

    if (!templatePath || !maskPath) {
      console.error('Template veya mask path bulunamadı:', { templatePath, maskPath });
      return;
    }

    try {
      const [phoneCase, maskImage] = await Promise.all([
        new Promise((resolve, reject) => {
          fabric.Image.fromURL(templatePath, img => {
            if (!img) {
              reject(new Error('Template image yüklenemedi'));
              return;
            }
            resolve(img);
          }, { crossOrigin: 'anonymous' });
        }),
        new Promise((resolve, reject) => {
          fabric.Image.fromURL(maskPath, img => {
            if (!img) {
              reject(new Error('Mask image yüklenemedi'));
              return;
            }
            resolve(img);
          }, { crossOrigin: 'anonymous' });
        })
      ]);

      // Görseli canvas boyutuna göre ayarla
      const targetHeight = canvas.height * 0.8;
      const scale = targetHeight / phoneCase.height;
      
      // Ana telefon kılıfı görselini ayarla
      phoneCase.scale(scale);
      phoneCase.set({
        left: (canvas.width - phoneCase.getScaledWidth()) / 2,
        top: (canvas.height - phoneCase.getScaledHeight()) / 2,
        selectable: false,
        evented: false,
        hoverCursor: 'default',
        isTemplate: true // Template olduğunu belirt
      });

      // Maske görselini ayarla
      maskImage.scale(scale);
      maskImage.set({
        left: (canvas.width - maskImage.getScaledWidth()) / 2,
        top: (canvas.height - maskImage.getScaledHeight()) / 2,
        selectable: false,
        evented: false,
        hoverCursor: 'default',
        isTemplate: true // Template olduğunu belirt
      });

      // Tasarım alanını oluştur
      const designArea = new fabric.Rect({
        left: 0,
        top: 0,
        width: canvas.width,
        height: canvas.height,
        fill: 'rgba(255, 255, 255, 0)',
        selectable: false,
        evented: false,
        hoverCursor: 'default',
        isTemplate: true // Template olduğunu belirt
      });

      // Maskeleme işlemi için clipPath oluştur
      const clipPath = new fabric.Group([maskImage], {
        absolutePositioned: true
      });

      // Tasarım alanına maskeyi uygula
      designArea.clipPath = clipPath;

      // Canvas'ı temizle
      canvas.clear();

      // Canvas'a öğeleri ekle
      canvas.add(phoneCase);
      canvas.add(designArea);
      
      // Template öğelerini en alta sabitle
      phoneCase.moveTo(0);
      designArea.moveTo(1);

      // Canvas'ı yenile
      canvas.renderAll();
      
      console.log('Template başarıyla yüklendi');
    } catch (error) {
      console.error('Template yüklenirken hata:', error);
      toast.error('Telefon şablonu yüklenirken bir hata oluştu');
    }
  };

  // Görsellere ortak özellikler ekle
  const setCommonObjectProperties = (obj) => {
    obj.set({
      cornerSize: 12,
      cornerStyle: 'circle',
      cornerColor: 'rgba(0,0,0,0.5)',
      cornerStrokeColor: '#aaa',
      transparentCorners: false,
      borderColor: 'rgba(0,0,0,0.3)',
      borderScaleFactor: 1,
      padding: 5,
      rotatingPointOffset: 40,
      hasRotatingPoint: true,
      centeredRotation: true,
      snapAngle: 45,
      snapThreshold: 10,
      lockSkewingX: true,
      lockSkewingY: true
    });

    // Nesne olaylarını temizle ve yeniden ekle
    obj.off('moving');
    obj.off('scaling');
    obj.off('rotating');
  };

  // Canvas başlatma ve kendin tasarla ücreti yükleme
  useEffect(() => {
    // Kendin tasarla ücretini yükle
    const loadKendinTasarlaFee = async () => {
      try {
        // Cache'i temizle ve yeniden yükle
        parameterService.clearCache();
        const fee = await parameterService.getKendinTasarlaFee();
        console.log('Yüklenen kendin tasarla ücreti:', fee);
        if (fee !== null) {
          setKendinTasarlaFee(fee);
        }
      } catch (error) {
        console.error('Kendin tasarla ücreti yüklenirken hata:', error);
      }
    };

    loadKendinTasarlaFee();

    const initCanvas = () => {
      const canvasElement = document.getElementById('design-canvas');
      if (!canvasElement) {
        console.error('Canvas element not found');
        return;
      }

      if (fabricRef.current) {
        fabricRef.current.dispose();
      }

      // Dinamik canvas boyutları
      const upperCanvas = document.querySelector('.upper-canvas');
      const CANVAS_WIDTH = upperCanvas ? upperCanvas.width : (window.innerWidth <= 768 ? 380 : 800);
      const CANVAS_HEIGHT = upperCanvas ? upperCanvas.height : (window.innerWidth <= 768 ? 380 : 800);

      const newCanvas = new fabric.Canvas('design-canvas', {
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        backgroundColor: '#ffffff',
        selection: true,
        preserveObjectStacking: true,
        centeredRotation: true,
        centeredScaling: true,
        snapAngle: 45,
        snapThreshold: 10,
        selectionColor: 'rgba(100, 100, 255, 0.3)',
        selectionLineWidth: 1,
        selectionBorderColor: 'rgba(0,0,0,0.3)',
        selectionDashArray: [5, 5],
        rotationCursor: 'crosshair',
        hoverCursor: 'move',
        defaultCursor: 'default'
      });

      // Canvas referansını kaydet
      fabricRef.current = newCanvas;
      setCanvas(newCanvas);

      // Container genişliğini al ve ölçeklendirme hesapla
      const container = document.querySelector('.canvas-container');
      const containerWidth = container ? container.parentElement.clientWidth : CANVAS_WIDTH;
      let displayScale;

      if (window.innerWidth <= 768) {
        // Mobil görünümde container genişliğine göre ölçeklendirme
        displayScale = Math.min(containerWidth / CANVAS_WIDTH, 1);
        
        // Container boyutlarını güncelle
        container.style.width = `${CANVAS_WIDTH * displayScale}px`;
        container.style.height = `${CANVAS_HEIGHT * displayScale}px`;
        container.style.position = 'relative';
        container.style.margin = '0 auto';
        container.style.paddingBottom = '0';
      } else {
        // Desktop görünümü için mevcut mantık
        displayScale = Math.min(Math.max(containerWidth / CANVAS_WIDTH, 0.5), 1);
        container.style.width = `${CANVAS_WIDTH * displayScale}px`;
        container.style.height = `${CANVAS_HEIGHT * displayScale}px`;
        container.style.margin = '0 auto';
      }

      // Canvas'ı ölçeklendir
      newCanvas.setZoom(displayScale);
      newCanvas.setWidth(CANVAS_WIDTH * displayScale);
      newCanvas.setHeight(CANVAS_HEIGHT * displayScale);

      // Yeni nesne eklendiğinde tasarım alanının içinde kalmasını sağla
      newCanvas.on('object:added', (e) => {
        if (e.target && !e.target.clipPath && e.target.type !== 'rect') {
          const designArea = newCanvas.getObjects().find(obj => obj.type === 'rect' && obj.clipPath);
          if (designArea) {
            e.target.clipPath = designArea.clipPath;
          }
        }
      });

      // Canvas olaylarını dinle
      newCanvas.on('object:moving', (e) => {
        const obj = e.target;
        // Sınırları kontrol et
        const bound = obj.getBoundingRect();
        if (bound.left < 0) {
          obj.left = obj.left - bound.left;
        }
        if (bound.top < 0) {
          obj.top = obj.top - bound.top;
        }
        if (bound.left + bound.width > newCanvas.width) {
          obj.left = newCanvas.width - bound.width;
        }
        if (bound.top + bound.height > newCanvas.height) {
          obj.top = newCanvas.height - bound.height;
        }
      });

      // Pencere yeniden boyutlandırma olayını dinle
      const handleResize = () => {
        const container = document.querySelector('.canvas-container');
        if (container) {
          const containerWidth = container.parentElement.clientWidth;
          let displayScale;

          if (window.innerWidth <= 768) {
            // Mobil görünümde container genişliğine göre ölçeklendirme
            displayScale = Math.min(containerWidth / CANVAS_WIDTH, 1);
            
            // Container boyutlarını güncelle
            container.style.width = `${CANVAS_WIDTH * displayScale}px`;
            container.style.height = `${CANVAS_HEIGHT * displayScale}px`;
            container.style.position = 'relative';
            container.style.margin = '0 auto';
            container.style.paddingBottom = '0';
          } else {
            // Desktop görünümü için mevcut mantık
            displayScale = Math.min(Math.max(containerWidth / CANVAS_WIDTH, 0.5), 1);
            container.style.width = `${CANVAS_WIDTH * displayScale}px`;
            container.style.height = `${CANVAS_HEIGHT * displayScale}px`;
            container.style.margin = '0 auto';
          }

          // Canvas'ı ölçeklendir
          newCanvas.setZoom(displayScale);
          newCanvas.setWidth(CANVAS_WIDTH * displayScale);
          newCanvas.setHeight(CANVAS_HEIGHT * displayScale);
          newCanvas.renderAll();
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (fabricRef.current) {
          const canvas = fabricRef.current;
          canvas.getObjects().forEach((obj) => {
            canvas.remove(obj);
          });
          canvas.dispose();
          fabricRef.current = null;
          setCanvas(null);
        }
      };
    };

    // Canvas'ı başlat
    const timeoutId = setTimeout(initCanvas, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Telefon modellerini yükle
  useEffect(() => {
    const fetchPhoneModels = async () => {
      try {
        // Kendin tasarla için sublimasyon (product_type_id = 1) telefon modellerini getir
        const response = await api.get('/phone-models?product_type_id=1');
        setPhoneModels(response.data);
        
        // Check if we have a selected model ID from the navigation state
        const selectedModelId = state?.selectedModelId;
        const selectedColorId = state?.selectedColorId;
        
        if (selectedModelId) {
          // Find the model from the response
          const model = response.data.find(m => m.id === selectedModelId);
          
          if (model) {
            setSelectedModel(model.id.toString());
            
            // Set colors for the selected model
            if (model.colors && model.colors.length > 0) {
              setSelectedModelColors(model.colors);
              
              // If a color was also selected, use it
              if (selectedColorId) {
                const color = model.colors.find(c => c.id === selectedColorId);
                if (color) {
                  setSelectedColor(color.id.toString());
                  setSelectedColorName(color.name);
                } else {
                  // Default to first color if the passed color ID isn't found
                  setSelectedColor(model.colors[0].id.toString());
                  setSelectedColorName(model.colors[0].name);
                }
              } else {
                // Default to first color if no color ID was passed
                setSelectedColor(model.colors[0].id.toString());
                setSelectedColorName(model.colors[0].name);
              }
            }
            
            // Load the phone template for the selected model
            const loadTemplate = async () => {
              const templateData = await fetchPhoneModelTemplates(model.id);
              if (templateData) {
                await loadPhoneTemplate(templateData.templateUrl, templateData.maskUrl);
              }
            };
            
            if (canvas) {
              loadTemplate();
            }
          }
        } else {
          // Default behavior if no model was selected
          // Akıllı model ve renk seçimi - stok durumuna göre
          if (response.data.length > 0) {
            const selectedModelAndColor = await selectBestAvailableModelAndColor(response.data);
            
            if (selectedModelAndColor) {
              setSelectedModel(selectedModelAndColor.model.id.toString());
              setSelectedModelColors(selectedModelAndColor.model.colors);
              setSelectedColor(selectedModelAndColor.color.id.toString());
              setSelectedColorName(selectedModelAndColor.color.name);
              
              // Telefon şablonunu yükle
              const templateData = await fetchPhoneModelTemplates(selectedModelAndColor.model.id);
              await loadPhoneTemplate(templateData.templateUrl, templateData.maskUrl);
            } else {
              // Hiçbir model+renk stokta yoksa fallback olarak ilk modeli seç
              const firstModel = response.data[0];
              setSelectedModel(firstModel.id.toString());
              
              if (firstModel.colors && firstModel.colors.length > 0) {
                setSelectedModelColors(firstModel.colors);
                setSelectedColor(firstModel.colors[0].id.toString());
                setSelectedColorName(firstModel.colors[0].name);
              }
              
              const templateData = await fetchPhoneModelTemplates(firstModel.id);
              await loadPhoneTemplate(templateData.templateUrl, templateData.maskUrl);
            }
          }
        }
      } catch (error) {
        console.error('Telefon modelleri yüklenirken hata:', error);
        toast.error('Telefon modelleri yüklenirken bir hata oluştu');
      }
    };

    if (canvas) {
      fetchPhoneModels();
    }
  }, [canvas, state?.selectedModelId, state?.selectedColorId]);

  // Tasarımları yükle
  useEffect(() => {
    const fetchDesigns = async () => {
      setIsLoadingDesigns(true);
      try {
        const response = await api.get('/design-library');
        setDesigns(response.data);
      } catch (error) {
        console.error('Error fetching designs:', error);
        toast.error('Tasarımlar yüklenirken bir hata oluştu');
      } finally {
        setIsLoadingDesigns(false);
      }
    };

    fetchDesigns();
  }, []);

  // Geçmişe kaydetme
  const saveToHistory = () => {
    if (!canvas) return;

    const json = canvas.toJSON(['evented', 'selectable', 'type']);
    const activeObject = canvas.getActiveObject();
    const activeObjectIndex = activeObject ? canvas.getObjects().indexOf(activeObject) : -1;

    // Geçmiş durumu kaydet
    const historyState = {
      canvasState: json,
      activeObjectIndex: activeObjectIndex,
      filterValues: { ...objectFilters[activeObject?.id] || {} }
    };

    // Yeni geçmiş dizisini oluştur
    let newHistory;
    if (historyIndex < history.length - 1) {
      // Eğer geri alınmış durumdaysak, sonraki adımları sil
      newHistory = [...history.slice(0, historyIndex + 1), historyState];
    } else {
      newHistory = [...history, historyState];
    }

    // Maksimum geçmiş adımını kontrol et
    if (newHistory.length > maxHistorySteps) {
      newHistory = newHistory.slice(newHistory.length - maxHistorySteps);
    }

    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Geri alma
  const undo = () => {
    if (!canvas || historyIndex <= 0) return;

    const newIndex = historyIndex - 1;
    const prevState = history[newIndex];

    if (prevState) {
      canvas.loadFromJSON(prevState.canvasState, () => {
        // Filtre değerlerini geri yükle
        setObjectFilters(prev => ({
          ...prev,
          ...prevState.filterValues
        }));

        // Aktif nesneyi geri yükle
        const objects = canvas.getObjects();
        if (prevState.activeObjectIndex >= 0 && objects[prevState.activeObjectIndex]) {
          const objectToSelect = objects[prevState.activeObjectIndex];
          canvas.setActiveObject(objectToSelect);
          setSelectedObject(objectToSelect);
          setShowEditTools(true);
        } else {
          // Eğer önceki durumda seçili nesne yoksa, son eklenen kullanıcı nesnesini seç
          const lastUserObject = objects.filter(obj => obj.type !== 'rect' && obj.evented !== false).pop();
          if (lastUserObject) {
            canvas.setActiveObject(lastUserObject);
            setSelectedObject(lastUserObject);
            setShowEditTools(true);
          }
        }

        canvas.renderAll();
        setHistoryIndex(newIndex);
      });
    }
  };

  // İleri alma
  const redo = () => {
    if (!canvas || historyIndex >= history.length - 1) return;

    const newIndex = historyIndex + 1;
    const nextState = history[newIndex];

    if (nextState) {
      canvas.loadFromJSON(nextState.canvasState, () => {
        // Filtre değerlerini geri yükle
        setObjectFilters(prev => ({
          ...prev,
          ...nextState.filterValues
        }));

        // Aktif nesneyi geri yükle
        const objects = canvas.getObjects();
        if (nextState.activeObjectIndex >= 0 && objects[nextState.activeObjectIndex]) {
          const objectToSelect = objects[nextState.activeObjectIndex];
          canvas.setActiveObject(objectToSelect);
          setSelectedObject(objectToSelect);
          setShowEditTools(true);
        } else {
          // Eğer sonraki durumda seçili nesne yoksa, son eklenen kullanıcı nesnesini seç
          const lastUserObject = objects.filter(obj => obj.type !== 'rect' && obj.evented !== false).pop();
          if (lastUserObject) {
            canvas.setActiveObject(lastUserObject);
            setSelectedObject(lastUserObject);
            setShowEditTools(true);
          }
        }

        canvas.renderAll();
        setHistoryIndex(newIndex);
      });
    }
  };

  // Canvas değişikliklerini izle
  useEffect(() => {
    if (canvas) {
      const handleCanvasModified = () => {
        const activeObject = canvas.getActiveObject();
        if (activeObject && activeObject.type !== 'rect' && activeObject.evented !== false) {
          setSelectedObject(activeObject);
          setShowEditTools(true);
        }
        saveToHistory();
      };

      canvas.on('object:modified', handleCanvasModified);
      canvas.on('object:added', handleCanvasModified);
      canvas.on('object:removed', handleCanvasModified);

      return () => {
        canvas.off('object:modified', handleCanvasModified);
        canvas.off('object:added', handleCanvasModified);
        canvas.off('object:removed', handleCanvasModified);
      };
    }
  }, [canvas]);

  // Resim ekleme fonksiyonunu güncelle
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Dosya boyutunu kontrol et
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 15) {
      toast.error('Dosya boyutu çok büyük (maximum 15MB). Lütfen daha küçük bir dosya seçin.');
      e.target.value = '';
      return;
    }

    const MAX_SIZE = 2400;
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        
        // Boyut kontrolü ve yeniden boyutlandırma
        if (width > MAX_SIZE || height > MAX_SIZE) {
          const ratio = Math.min(MAX_SIZE / width, MAX_SIZE / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
          console.log(`Resizing image from ${img.width}x${img.height} to ${width}x${height}`);
        }

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = height;
        const ctx = tempCanvas.getContext('2d');
        
        if (file.type === "image/jpeg" || file.type === "image/jpg") {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(img, 0, 0, width, height);

        const quality = (fileSizeMB > 5) ? 0.75 : (fileSizeMB > 2) ? 0.85 : 0.92;
        
        fabric.Image.fromURL(tempCanvas.toDataURL('image/webp', quality), (fabricImage) => {
          const phoneCase = canvas.getObjects().find(obj => !obj.evented);
          if (!phoneCase) {
            toast.error('Telefon kılıfı şablonu bulunamadı');
            return;
          }

          const scale = Math.min(
            (phoneCase.getScaledWidth() * 0.8) / fabricImage.width,
            (phoneCase.getScaledHeight() * 0.8) / fabricImage.height
          );

          const uniqueId = `image_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

          fabricImage.set({
            id: uniqueId,
            scaleX: scale,
            scaleY: scale,
            left: phoneCase.left + (phoneCase.getScaledWidth() - fabricImage.width * scale) / 2,
            top: phoneCase.top + (phoneCase.getScaledHeight() - fabricImage.height * scale) / 2,
            evented: true,
            type: 'image'
          });

          setObjectFilters(prev => ({
            ...prev,
            [uniqueId]: {
              brightness: 0,
              contrast: 0,
              saturation: 0,
              blur: 0,
              sharpen: 0
            }
          }));

          canvas.add(fabricImage);
          canvas.setActiveObject(fabricImage);
          setSelectedObject(fabricImage);
          setShowEditTools(true);

          setTimeout(() => {
            setSelectedObject(fabricImage);
            setShowEditTools(true);
            canvas.requestRenderAll();
            // Canvas alanına yumuşak kaydırma
            const canvasArea = document.querySelector('.canvas-area');
            if (canvasArea) {
              canvasArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }, { crossOrigin: 'anonymous' });
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // WebP desteğini kontrol et
  const isWebPSupported = () => {
    const canvas = document.createElement('canvas');
    if (!canvas.toDataURL) {
      return false;
    }
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  };

  // Tasarımı sepete ekleme
  const addDesignToCart = async () => {
    try {
      setIsLoading(true);
      setLoadingStatus('Tasarımınız hazırlanıyor...');
      
      if (!canvas || !selectedModel || !selectedColor) {
        toast.error('Lütfen telefon modeli ve renk seçimi yapınız');
        setIsLoading(false);
        return;
      }

      // Seçilen modelin detaylarını bul
      const selectedModelDetails = phoneModels.find(model => model.id === parseInt(selectedModel));
      if (!selectedModelDetails) {
        throw new Error('Telefon modeli bulunamadı');
      }

      // Stok kontrolü yap
      setLoadingStatus('Stok durumu kontrol ediliyor...');
      // Ürün, model ve renk stok kontrolü için API çağrısı (kendin tasarla için özel endpoint)
      const stockResponse = await api.get(`/phone-models/${selectedModel}/colors/${selectedColor}/custom-design-stock?product_type_id=1`);
      const availableStock = stockResponse.data.stock || 0;
      
      // Sepetteki mevcut model ve renk kombinasyonunu bul - tüm ürünleri kontrol et
      const cartItemsWithSameModelAndColor = cart.filter(item => 
        item.phone_model_id === parseInt(selectedModel) && 
        item.color_id === parseInt(selectedColor)
      );
      
      // Sepetteki toplam miktarı hesapla
      const cartQuantity = cartItemsWithSameModelAndColor.reduce((sum, item) => sum + item.quantity, 0);
      
      // Toplam gerekli stok
      const totalRequiredStock = cartQuantity + 1; // 1 = yeni eklenen ürün
      
      // Stok kontrolü
      if (totalRequiredStock > availableStock) {
        toast.error(`Bu model ve renk için yeterli stok bulunmamaktadır. Mevcut stok: ${availableStock}, Sepetteki miktar: ${cartQuantity}`);
        setIsLoading(false);
        return;
      }

      // Şablon görsellerini çek
      setLoadingStatus('Telefon şablonu yükleniyor...');
      const templateData = await fetchPhoneModelTemplates(selectedModelDetails.id);
      if (!templateData) {
        throw new Error('Telefon modeli için şablon bulunamadı');
      }

      // Canvas oluşturma ve tasarım işlemleri...
      // 1. Tam tasarım için canvas (telefon kılıfı şablonu ile)
      setLoadingStatus('Tasarım görüntüleri oluşturuluyor...');
      const fullDesignCanvas = document.createElement('canvas');
      const scale = 3; // Yüksek çözünürlük için ölçek
      fullDesignCanvas.width = canvas.width * scale;
      fullDesignCanvas.height = canvas.height * scale;
      const fullDesignCtx = fullDesignCanvas.getContext('2d');

      // Beyaz arka plan ekle
      fullDesignCtx.fillStyle = '#ffffff';
      fullDesignCtx.fillRect(0, 0, fullDesignCanvas.width, fullDesignCanvas.height);

      // Canvas'taki telefon kılıfı şablonunu bul
      const phoneCase = canvas.getObjects().find(obj => !obj.evented);
      if (!phoneCase) {
        throw new Error('Telefon kılıfı şablonu bulunamadı');
      }

      // Telefon kılıfı şablonunu yükle ve çiz
      setLoadingStatus('Telefon şablonu uygulanıyor...');
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const phoneCaseWidth = phoneCase.getScaledWidth() * scale;
          const phoneCaseHeight = phoneCase.getScaledHeight() * scale;
          const centerX = (fullDesignCanvas.width - phoneCaseWidth) / 2;
          const centerY = (fullDesignCanvas.height - phoneCaseHeight) / 2;

          fullDesignCtx.drawImage(
            img,
            centerX,
            centerY,
            phoneCaseWidth,
            phoneCaseHeight
          );
          resolve();
        };
        img.onerror = reject;
        img.src = templateData.templateUrl;
      });

      // 2. Sadece maskelenmiş tasarım için canvas
      const maskedDesignCanvas = document.createElement('canvas');
      maskedDesignCanvas.width = fullDesignCanvas.width;
      maskedDesignCanvas.height = fullDesignCanvas.height;
      const maskedDesignCtx = maskedDesignCanvas.getContext('2d');

      // Beyaz arka plan ekle
      maskedDesignCtx.fillStyle = '#ffffff';
      maskedDesignCtx.fillRect(0, 0, maskedDesignCanvas.width, maskedDesignCanvas.height);

      // Her iki canvas'a tasarım öğelerini çiz
      const objects = canvas.getObjects();
      const drawObjects = (ctx) => {
        for (const obj of objects) {
          // Sadece görünür ve template olmayan nesneleri çiz
          if (obj !== phoneCase && obj.type !== 'rect' && obj.visible !== false) {
            ctx.save();

            // Objenin sınırlarını al
            const boundingRect = obj.getBoundingRect();
            
            // Objenin merkez noktasını hesapla
            const centerX = boundingRect.left * scale + (boundingRect.width * scale / 2);
            const centerY = boundingRect.top * scale + (boundingRect.height * scale / 2);

            // Transformasyonları uygula
            ctx.translate(centerX, centerY);
            ctx.rotate(obj.angle * Math.PI / 180);

            // Flip durumlarını uygula
            if (obj.flipX) ctx.scale(-1, 1);
            if (obj.flipY) ctx.scale(1, -1);
            
            if (obj.type === 'image') {
              // Görsel için ölçeklendirilmiş boyutları hesapla
              const scaledWidth = obj.width * obj.scaleX * scale;
              const scaledHeight = obj.height * obj.scaleY * scale;

              try {
                // Flip durumlarını hesaba katarak görseli çiz
                ctx.drawImage(
                  obj._element,
                  -scaledWidth / 2,
                  -scaledHeight / 2,
                  scaledWidth,
                  scaledHeight
                );
              } catch (error) {
                console.error('Error drawing image:', error);
              }
            } else if (obj.type === 'i-text') {
              ctx.fillStyle = obj.fill;
              ctx.font = `${obj.fontSize * scale}px ${obj.fontFamily}`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(obj.text, 0, 0);
            }
            
            ctx.restore();
          }
        }
      };

      // Her iki canvas'a tasarımları çiz
      drawObjects(maskedDesignCtx);
      
      // 3. Sadece orijinal tasarım için canvas (referanssız ve maskesiz)
      const originalDesignCanvas = document.createElement('canvas');
      originalDesignCanvas.width = fullDesignCanvas.width;
      originalDesignCanvas.height = fullDesignCanvas.height;
      const originalDesignCtx = originalDesignCanvas.getContext('2d');
      
      // Beyaz arka plan ekle
      originalDesignCtx.fillStyle = '#ffffff';
      originalDesignCtx.fillRect(0, 0, originalDesignCanvas.width, originalDesignCanvas.height);
      
      // Sadece tasarım öğelerini çiz (telefon kılıfı şablonu ve maske olmadan)
      drawObjects(originalDesignCtx);
      
      // Orijinal tasarımı WebP olarak dışa aktar (optimize edilmiş kalite)
      setLoadingStatus('Yüksek kaliteli tasarım görüntüsü hazırlanıyor...');
      
      // Optimize edilmiş kalite (100% yerine 70% kullanarak dosya boyutunu azalt)
      const originalDesignImageWebP = originalDesignCanvas.toDataURL('image/webp', 0.85);

      // Maske görselini yükle ve uygula
      setLoadingStatus('Maske uygulanıyor...');
      await new Promise((resolve, reject) => {
        const maskImg = new Image();
        maskImg.crossOrigin = 'anonymous';
        maskImg.onload = () => {
          const phoneCaseWidth = phoneCase.getScaledWidth() * scale;
          const phoneCaseHeight = phoneCase.getScaledHeight() * scale;
          const centerX = (maskedDesignCanvas.width - phoneCaseWidth) / 2;
          const centerY = (maskedDesignCanvas.height - phoneCaseHeight) / 2;

          // Maskelenmiş tasarımı oluştur
          maskedDesignCtx.globalCompositeOperation = 'destination-in';
          maskedDesignCtx.drawImage(
            maskImg,
            centerX,
            centerY,
            phoneCaseWidth,
            phoneCaseHeight
          );

          // Maskelenmiş tasarımı tam tasarıma ekle
          maskedDesignCtx.globalCompositeOperation = 'source-over';
          fullDesignCtx.drawImage(maskedDesignCanvas, 0, 0);

          resolve();
        };
        maskImg.onerror = reject;
        maskImg.src = templateData.maskUrl;
      });

      // Her iki canvas'ı WebP olarak dışa aktar
      setLoadingStatus('Görüntüler optimize ediliyor...');
      const fullDesignImageWebP = fullDesignCanvas.toDataURL('image/webp', 0.85);
      const maskedDesignImageWebP = maskedDesignCanvas.toDataURL('image/webp', 0.85);

      // API'ye tasarımı gönder ve ürün oluştur
      setLoadingStatus('Tasarımınız sunucuya yükleniyor...');
      
      // Görsel boyutlarını logla (debugging için)
      const logImageSize = (imageStr) => {
        if (!imageStr) return '0 KB';
        // data:image/webp;base64, kısmını çıkar
        const base64Data = imageStr.replace(/^data:image\/\w+;base64,/, '');
        // Base64 boyutunu hesapla
        const size = (base64Data.length * 0.75) / 1024; // Yaklaşık KB cinsinden boyut
        return size < 1024 ? `${size.toFixed(2)} KB` : `${(size / 1024).toFixed(2)} MB`;
      };
      
      console.log(`Full design image size: ${logImageSize(fullDesignImageWebP)}`);
      console.log(`Masked design image size: ${logImageSize(maskedDesignImageWebP)}`);
      console.log(`Original design image size: ${logImageSize(originalDesignImageWebP)}`);
      
      const response = await api.post('/custom-designs', {
        image: fullDesignImageWebP,
        mask_image: maskedDesignImageWebP,
        no_mask: originalDesignImageWebP, // Orijinal tasarım görselini ekle (referanssız ve maskesiz)
        phone_model_id: parseInt(selectedModel),
        color_id: parseInt(selectedColor),
        user_id: user?.id || null,
        quantity: 1,
        price: kendinTasarlaFee,
        is_approval: isApprovalChecked // Yayınlama onayı durumunu ekle
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        // Büyük dosyalar için timeout süresini uzat
        timeout: 60000, // 60 saniye
      });

      console.log('API response:', response.data); // API yanıtını kontrol et

      if (response.data && response.data.success) {
        // Sepete eklenecek ürün bilgilerini hazırla
        const product = {
          id: response.data.id,
          name: 'Müşteri Özel Tasarım Kılıfı',
          description: 'Kişiselleştirilmiş telefon kılıfı',
          price: kendinTasarlaFee,
          images: response.data.image,
          mask_image: response.data.mask_image,
          no_mask: response.data.no_mask,
          is_custom: true
        };

        console.log('Product with no_mask:', product.no_mask); // no_mask değerini kontrol et

        // Seçili telefon modelini bul
        const phoneModel = phoneModels.find(model => model.id === parseInt(selectedModel));
        
        // Seçili rengi bul
        const selectedColorObj = selectedModelColors.find(color => color.id === parseInt(selectedColor));

        // Sepete ekle
        setLoadingStatus('Tasarımınız sepete ekleniyor...');
        await addToCart(product, phoneModel, selectedColorObj, 1);
        toast.success('Tasarım sepete eklendi');
        
        // Sepet sayfasına otomatik yönlendir
        navigate('/cart');
      } else {
        throw new Error(response.data?.message || 'Tasarım oluşturulamadı');
      }
    } catch (error) {
      console.error('Error adding design to cart:', error);
      toast.error(error.response?.data?.message || 'Tasarım sepete eklenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
      setLoadingStatus('');
    }
  };


  // Kütüphaneden görsel ekleme
  const addLibraryImage = (imageUrl) => {
    if (!canvas) return;

    fabric.Image.fromURL(imageUrl, (img) => {
      // Telefon kılıfı şablonunu bul
      const phoneCase = canvas.getObjects().find(obj => !obj.evented);
      if (!phoneCase) {
        toast.error('Telefon kılıfı şablonu bulunamadı');
        return;
      }

      // Telefon kılıfı şablonunun boyutlarını al
      const phoneCaseWidth = phoneCase.getScaledWidth();
      const phoneCaseHeight = phoneCase.getScaledHeight();
      const phoneCaseLeft = phoneCase.left;
      const phoneCaseTop = phoneCase.top;

      // Görseli telefon kılıfı boyutuna göre ölçekle
      const scale = Math.min(
        (phoneCaseWidth * 0.8) / img.width,
        (phoneCaseHeight * 0.8) / img.height
      );

      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;

      // Benzersiz bir ID oluştur
      const uniqueId = `image_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      img.set({
        id: uniqueId,
        scaleX: scale,
        scaleY: scale,
        left: phoneCaseLeft + (phoneCaseWidth - scaledWidth) / 2,
        top: phoneCaseTop + (phoneCaseHeight - scaledHeight) / 2,
        originX: 'left',
        originY: 'top',
        evented: true,
        type: 'image'
      });

      // Varsayılan filtre değerlerini ayarla
      setObjectFilters(prev => ({
        ...prev,
        [uniqueId]: {
          brightness: 0,
          contrast: 0,
          saturation: 0,
          blur: 0,
          sharpen: 0
        }
      }));

      setCommonObjectProperties(img);
      canvas.add(img);
      canvas.setActiveObject(img);
      setSelectedObject(img);
      setShowEditTools(true);
      canvas.renderAll();
      saveToHistory();

      // Düzenleme araçlarının görünürlüğünü zorla
      setTimeout(() => {
        setSelectedObject(img);
        setShowEditTools(true);
        canvas.requestRenderAll();
      }, 100);
    }, { crossOrigin: 'anonymous' });
  };

  // Model seçimi değiştiğinde
  const handleModelSelect = async (e) => {
    const modelId = e.target.value;
    if (!modelId) return;

    setSelectedModel(modelId);
    setCurrentColorStockInfo(null); // Renk stok bilgisini sıfırla
    
    try {
      // Seçilen modeli bul
      const selectedPhoneModel = phoneModels.find(model => model.id.toString() === modelId);
      
      if (selectedPhoneModel) {
        // Modelin renklerini ayarla
        if (selectedPhoneModel.colors && selectedPhoneModel.colors.length > 0) {
          setSelectedModelColors(selectedPhoneModel.colors);
          
          // İlk rengi seç
          const firstColorId = selectedPhoneModel.colors[0].id.toString();
          setSelectedColor(firstColorId);
          setSelectedColorName(selectedPhoneModel.colors[0].name);
        }
        
        // 1. ÖNCELİK: Telefon şablonunu hemen yükle (görsel deneyim için)
        const templateData = await fetchPhoneModelTemplates(parseInt(modelId));
        await loadPhoneTemplate(templateData.templateUrl, templateData.maskUrl);
        
        // 2. SONRA: Stok kontrolü yap (arka planda)
        if (selectedPhoneModel.colors && selectedPhoneModel.colors.length > 0) {
          const firstColorId = selectedPhoneModel.colors[0].id.toString();
          setLoadingStock(true);
          try {
            const stockResponse = await api.get(`/phone-models/${modelId}/colors/${firstColorId}/custom-design-stock?product_type_id=1`);
            const availableStock = stockResponse.data.stock || 0;
            
            // Stok bilgilerini güncelle
            setCurrentColorStockInfo({
              colorId: firstColorId,
              available: availableStock > 0,
              stock: availableStock
            });
            
            // Stokta yoksa kullanıcıyı bilgilendir
            if (availableStock <= 0) {
              toast.warning(`${selectedPhoneModel.colors[0].name} rengi için stok bulunmamaktadır.`);
            }
          } catch (error) {
            console.error('Renk stok kontrolü hatası:', error);
            setCurrentColorStockInfo({
              colorId: firstColorId,
              available: false,
              stock: 0
            });
          } finally {
            setLoadingStock(false);
          }
        }
      }
    } catch (error) {
      console.error('Model yükleme hatası:', error);
      toast.error('Model yüklenirken bir hata oluştu');
    }
  };

  // Renk seçimi için yeni fonksiyon
  const handleColorSelect = async (colorId, colorName) => {
    // Seçilmeye çalışılan renk için stok bilgisini kontrol et
    const colorKey = `${selectedModel}-${colorId}`;
    const colorStockInfo = stockInfo[colorKey];
    
    // Eğer stok bilgisi varsa ve stokta yoksa (sepetteki ürünler stok limitini doldurmuşsa), seçime izin verme
    if (colorStockInfo && !colorStockInfo.available) {
      toast.error(`${colorName} rengi için yeterli stok bulunmamaktadır. (Sepetteki miktar: ${cartItemsCount[colorKey] || 0}, Mevcut stok: ${colorStockInfo.stock})`);
      return;
    }
    
    setSelectedColor(colorId.toString());
    setSelectedColorName(colorName);
    
    // Eğer model seçilmişse stok bilgisini kontrol et
    if (selectedModel) {
      setLoadingStock(true);
      try {
        const stockResponse = await api.get(`/phone-models/${selectedModel}/colors/${colorId}/custom-design-stock?product_type_id=1`);
        const availableStock = stockResponse.data.stock || 0;
        
        // Sepetteki bu model ve renk kombinasyonunu bul - tüm ürünleri kontrol et
        const cartItemsWithSameModelAndColor = cart.filter(item => 
          item.phone_model_id === parseInt(selectedModel) && 
          item.color_id === parseInt(colorId)
        );
        
        // Sepetteki toplam miktarı hesapla
        const cartQuantity = cartItemsWithSameModelAndColor.reduce((sum, item) => sum + item.quantity, 0);
        
        // Kalan stok miktarını hesapla
        const remainingStock = Math.max(0, availableStock - cartQuantity);
        
        // Stok bilgilerini güncelle
        setCurrentColorStockInfo({
          colorId: colorId.toString(),
          available: remainingStock > 0,
          stock: availableStock,
          cartQuantity: cartQuantity,
          remainingStock: remainingStock
        });
        
        // Stokta yoksa kullanıcıyı bilgilendir
        if (remainingStock <= 0) {
          toast.warning(`${colorName} rengi için yeterli stok bulunmamaktadır. (Sepetteki miktar: ${cartQuantity}, Mevcut stok: ${availableStock})`);
        }
      } catch (error) {
        console.error('Renk stok kontrolü hatası:', error);
        setCurrentColorStockInfo({
          colorId: colorId.toString(),
          available: false,
          stock: 0,
          cartQuantity: 0,
          remainingStock: 0
        });
      } finally {
        setLoadingStock(false);
      }
    }
  };

  // Renk tipini belirleyen yardımcı fonksiyon
  const getColorType = (colorCode) => {
    // Saydam renk kontrolü
    if (!colorCode || colorCode === 'transparent' || colorCode.toLowerCase().includes('rgba') && colorCode.endsWith(',0)')) {
      return 'transparent';
    }
    
    // Beyaz renk kontrolü
    if (colorCode === '#ffffff' || colorCode === '#fff' || colorCode.toLowerCase() === 'white') {
      return 'white';
    }
    
    // Açık/koyu renk kontrolü (tik işareti rengi için)
    const isLight = (colorCode) => {
      // Hex renk kodunu RGB'ye çevir
      let r, g, b;
      
      if (colorCode.startsWith('#')) {
        const hex = colorCode.replace('#', '');
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      } else if (colorCode.startsWith('rgb')) {
        const rgb = colorCode.match(/\d+/g);
        r = parseInt(rgb[0]);
        g = parseInt(rgb[1]);
        b = parseInt(rgb[2]);
      } else {
        return false; // Bilinmeyen format
      }
      
      // Rengin parlaklığını hesapla (YIQ formülü)
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128; // 128'den büyükse açık renk
    };
    
    return isLight(colorCode) ? 'light' : 'dark';
  };

  const flipHorizontal = () => {
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      try {
        activeObject.set('flipX', !activeObject.flipX);
        canvas.renderAll();
        saveToHistory();
      } catch (error) {
        console.error('Yatay çevirme hatası:', error);
        toast.error('Görsel çevrilirken bir hata oluştu');
      }
    } else {
      toast.warning('Lütfen düzenlemek için bir görsel seçin');
    }
  };

  const flipVertical = () => {
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      try {
        activeObject.set('flipY', !activeObject.flipY);
        canvas.renderAll();
        saveToHistory();
      } catch (error) {
        console.error('Dikey çevirme hatası:', error);
        toast.error('Görsel çevrilirken bir hata oluştu');
      }
    } else {
      toast.warning('Lütfen düzenlemek için bir görsel seçin');
    }
  };

  const rotate = (angle) => {
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      try {
        activeObject.rotate((activeObject.angle || 0) + angle);
        canvas.renderAll();
        saveToHistory();
      } catch (error) {
        console.error('Döndürme hatası:', error);
        toast.error('Görsel döndürülürken bir hata oluştu');
      }
    } else {
      toast.warning('Lütfen düzenlemek için bir görsel seçin');
    }
  };

  const applyFilter = (filter) => {
    const activeObject = canvas.getActiveObject();
    if (activeObject && activeObject.type === 'image') {
      // Mevcut ayarlamaları al
      const currentFilters = objectFilters[activeObject.id] || {
        brightness: 0,
        contrast: 0,
        saturation: 0,
        blur: 0,
        sharpen: 0
      };

      // Filtreleri temizle ama ayarlamaları koru
      activeObject.filters = [];
      
      // Yeni filtreyi uygula
      switch(filter) {
        case 'grayscale':
          activeObject.filters.push(new fabric.Image.filters.Grayscale());
          break;
        case 'sepia':
          activeObject.filters.push(new fabric.Image.filters.Sepia());
          break;
        case 'invert':
          activeObject.filters.push(new fabric.Image.filters.Invert());
          break;
        case 'vintage':
          // Vintage efekti için birkaç filtre kombinasyonu
          activeObject.filters.push(
            new fabric.Image.filters.Sepia(),
            new fabric.Image.filters.Contrast({ contrast: 0.15 }),
            new fabric.Image.filters.Brightness({ brightness: -0.1 })
          );
          break;
        case 'none':
          // Sadece filtreleri temizle, ayarlamaları koru
          break;
      }

      // Mevcut ayarlamaları yeniden uygula
      if (currentFilters.brightness !== 0) {
        activeObject.filters.push(
          new fabric.Image.filters.Brightness({ brightness: currentFilters.brightness / 100 })
        );
      }
      if (currentFilters.contrast !== 0) {
        activeObject.filters.push(
          new fabric.Image.filters.Contrast({ contrast: currentFilters.contrast / 100 })
        );
      }
      if (currentFilters.saturation !== 0) {
        activeObject.filters.push(
          new fabric.Image.filters.Saturation({ saturation: currentFilters.saturation / 100 })
        );
      }
      if (currentFilters.blur !== 0) {
        activeObject.filters.push(
          new fabric.Image.filters.Blur({ blur: currentFilters.blur / 10 })
        );
      }
      
      try {
        activeObject.applyFilters();
        canvas.renderAll();
        saveToHistory();
      } catch (error) {
        console.error('Filtre uygulama hatası:', error);
        toast.error('Filtre uygulanırken bir hata oluştu');
      }
    } else {
      toast.warning('Lütfen düzenlemek için bir görsel seçin');
    }
  };

  // Görsel ayarlamalarını güncelle
  const updateImageAdjustments = (type, value) => {
    const activeObject = canvas.getActiveObject();
    if (!activeObject || activeObject.type !== 'image' || !activeObject.id) return;

    // Mevcut filtreleri al
    const currentFilters = objectFilters[activeObject.id] || {
      brightness: 0,
      contrast: 0,
      saturation: 0,
      blur: 0,
      sharpen: 0,
      appliedFilters: [] // Uygulanan filtreleri takip etmek için yeni alan
    };

    // Yeni değeri güncelle
    const updatedFilters = {
      ...currentFilters,
      [type]: value
    };

    // State'i güncelle
    setObjectFilters(prev => ({
      ...prev,
      [activeObject.id]: updatedFilters
    }));

    // Mevcut filtreleri koru
    const existingFilters = activeObject.filters || [];
    const basicFilters = existingFilters.filter(filter => 
      filter instanceof fabric.Image.filters.Grayscale ||
      filter instanceof fabric.Image.filters.Sepia ||
      filter instanceof fabric.Image.filters.Invert
    );

    // Yeni filtre dizisini oluştur
    activeObject.filters = [...basicFilters]; // Önce mevcut temel filtreleri ekle

    // Ayarlamaları ekle
    if (updatedFilters.brightness !== 0) {
      activeObject.filters.push(
        new fabric.Image.filters.Brightness({ brightness: updatedFilters.brightness / 100 })
      );
    }
    if (updatedFilters.contrast !== 0) {
      activeObject.filters.push(
        new fabric.Image.filters.Contrast({ contrast: updatedFilters.contrast / 100 })
      );
    }
    if (updatedFilters.saturation !== 0) {
      activeObject.filters.push(
        new fabric.Image.filters.Saturation({ saturation: updatedFilters.saturation / 100 })
      );
    }
    if (updatedFilters.blur !== 0) {
      activeObject.filters.push(
        new fabric.Image.filters.Blur({ blur: updatedFilters.blur / 10 })
      );
    }

    // Filtreleri uygula
    activeObject.applyFilters();
    canvas.renderAll();
  };

  // Canvas seçim olayını dinle ve filtre değerlerini güncelle
  useEffect(() => {
    if (canvas) {
      const handleSelection = (e) => {
        const selectedObject = e.selected?.[0];
        if (selectedObject && selectedObject.type === 'image') {
          const objectId = selectedObject.id;
          
          // Mevcut filtre değerlerini kontrol et
          const currentFilters = objectFilters[objectId] || {
            brightness: 0,
            contrast: 0,
            saturation: 0,
            blur: 0,
            sharpen: 0
          };

          // State'i güncelle
          setObjectFilters(prev => ({
            ...prev,
            [objectId]: currentFilters
          }));

          // Seçilen objeyi ve düzenleme araçlarını göster
          setSelectedObject(selectedObject);
          setShowEditTools(true);
        } else {
          setSelectedObject(null);
          setShowEditTools(false);
        }
      };

      canvas.on('selection:created', handleSelection);
      canvas.on('selection:updated', handleSelection);
      canvas.on('selection:cleared', () => {
        setSelectedObject(null);
        setShowEditTools(false);
      });

      return () => {
        canvas.off('selection:created', handleSelection);
        canvas.off('selection:updated', handleSelection);
        canvas.off('selection:cleared');
      };
    }
  }, [canvas, objectFilters]);

  // Katmanları güncelle
  const updateLayers = () => {
    if (!canvas) return;
    
    const objects = canvas.getObjects();
    const newLayers = objects
      .filter(obj => obj.type !== 'rect' && obj.evented !== false) // Telefon şablonunu ve maskeyi hariç tut
      .map((obj, index) => {
        let layerData = {
          id: obj.id || `layer-${index}`,
          type: obj.type === 'i-text' ? 'text' : 'image',
          visible: obj.visible !== false,
          object: obj
        };

        if (obj.type === 'i-text') {
          layerData.text = obj.text;
        } else if (obj.type === 'image') {
          // Görsel için kaynak URL'i al
          layerData.src = obj._element?.src || obj.toDataURL('image/webp', 0.8);
        }

        return layerData;
      })
      .reverse(); // En üstteki katmanı ilk göster

    setLayers(newLayers);
  };

  // Canvas olaylarını dinle ve katmanları güncelle
  useEffect(() => {
    if (canvas) {
      const handleCanvasModified = () => {
        updateLayers();
      };

      const handleObjectMoving = (e) => {
        const obj = e.target;
        if (!obj.isTemplate) {
          // Template öğelerinin indexlerini bul
          const templateObjects = canvas.getObjects().filter(obj => obj.isTemplate);
          const minIndex = templateObjects.length;
          
          // Eğer obje template öğelerinin altına düşmeye çalışıyorsa, engelle
          const currentIndex = canvas.getObjects().indexOf(obj);
          if (currentIndex < minIndex) {
            obj.moveTo(minIndex);
          }
        }
      };

      canvas.on('object:added', handleCanvasModified);
      canvas.on('object:removed', handleCanvasModified);
      canvas.on('object:modified', handleCanvasModified);
      canvas.on('object:moving', handleObjectMoving);

      return () => {
        canvas.off('object:added', handleCanvasModified);
        canvas.off('object:removed', handleCanvasModified);
        canvas.off('object:modified', handleCanvasModified);
        canvas.off('object:moving', handleObjectMoving);
      };
    }
  }, [canvas]);

  // Katman seç
  const selectLayer = (layer) => {
    if (!canvas || !layer) return;
    
    const obj = layer.object;
    canvas.setActiveObject(obj);
    canvas.requestRenderAll();
    setSelectedLayer(layer.id);
    setSelectedObject(obj);
    setShowEditTools(true);
  };

  // Katman görünürlüğünü değiştir
  const toggleLayerVisibility = (layer) => {
    if (!canvas || !layer) return;
    
    const obj = layer.object;
    obj.visible = !obj.visible;
    canvas.requestRenderAll();
    updateLayers();
  };

  // Katmanı yukarı taşı fonksiyonunu güncelle
  const moveLayerUp = (layer) => {
    if (!canvas || !layer) return;
    
    const obj = layer.object;
    const currentIndex = canvas.getObjects().indexOf(obj);
    const maxIndex = canvas.getObjects().length - 1;
    
    if (currentIndex < maxIndex) {
      obj.bringForward();
      canvas.requestRenderAll();
      updateLayers();
    }
  };

  // Katmanı aşağı taşı fonksiyonunu güncelle
  const moveLayerDown = (layer) => {
    if (!canvas || !layer) return;
    
    const obj = layer.object;
    const currentIndex = canvas.getObjects().indexOf(obj);
    
    // Template öğelerinin indexlerini bul
    const templateObjects = canvas.getObjects().filter(obj => obj.isTemplate);
    const minIndex = templateObjects.length;
    
    if (currentIndex > minIndex) {
      obj.sendBackwards();
      canvas.requestRenderAll();
      updateLayers();
    }
  };

  // Katman silme
  const deleteLayer = (layer) => {
    if (!canvas || !layer) return;
    
    const obj = layer.object;
    canvas.remove(obj);
    canvas.requestRenderAll();
    updateLayers();
    setSelectedLayer(null);
    setSelectedObject(null);
    setShowEditTools(false);
  };

  // Katman Paneli Bileşeni
  const LayersPanel = () => (
    <div className="layers-panel">
      <h5>
        <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
        Katmanlar
      </h5>
      <div className="layer-list">
        {layers.length > 0 ? (
          layers.map((layer) => (
            <div
              key={layer.id}
              className={`layer-item ${selectedLayer === layer.id ? 'active' : ''}`}
              onClick={() => selectLayer(layer)}
            >
              <div className="layer-preview">
                {layer.type === 'image' && (
                  <img
                    src={layer.src}
                    alt={`Layer ${layer.id}`}
                    className="layer-thumbnail"
                  />
                )}
                {layer.type === 'text' && (
                  <div className="layer-text-preview">
                    <FontAwesomeIcon icon={faFont} />
                  </div>
                )}
              </div>
              <div className="layer-name">{layer.type === 'text' ? layer.text : `Görsel ${layer.id}`}</div>
              <div className="layer-controls">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLayerVisibility(layer);
                  }}
                  className={`visibility-toggle ${layer.visible !== false ? 'visible' : ''}`}
                >
                  <FontAwesomeIcon icon={layer.visible !== false ? faEye : faEyeSlash} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveLayerUp(layer);
                  }}
                >
                  <FontAwesomeIcon icon={faArrowUp} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveLayerDown(layer);
                  }}
                >
                  <FontAwesomeIcon icon={faArrowDown} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteLayer(layer);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-layers">Henüz katman eklenmemiş</div>
        )}
      </div>
    </div>
  );

  const handleColorInfoClick = (e) => {
    e.preventDefault();
    setShowColorInfo(true);
  };

  const handleCloseColorInfo = () => {
    setShowColorInfo(false);
  };

  const handleApprovalInfoClick = (e) => {
    e.preventDefault();
    setShowApprovalInfo(true);
  };

  const handleCloseApprovalInfo = () => {
    setShowApprovalInfo(false);
  };

  const handleApprovalChange = (e) => {
    setIsApprovalChecked(e.target.checked);
  };

  const handleFilteredImageReady = (imageUrl) => {
    setShowPhotoFilters(false);
    if (imageUrl) {
      addLibraryImage(imageUrl);
      toast.success('Filtrelenmiş görseliniz tasarıma eklendi!');
      // Canvas alanına yumuşak kaydırma
      setTimeout(() => {
        const canvasArea = document.querySelector('.canvas-area');
        if (canvasArea) {
          canvasArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleFilterSelect = (filter) => {
    console.log('Filter selected:', filter);
    // TODO: Filtre uygulama mantığı eklenecek.
    setShowPhotoFilters(false);
    toast.info(`${filter.asset_name} filtresi seçildi. Uygulama özelliği yakında eklenecektir.`);
  };

  // Renk seçenekleri için stok durumunu takip edecek useEffect
  useEffect(() => {
    if (selectedModel && selectedModelColors.length > 0) {
      const checkStockForAllColors = async () => {
        const updatedStockInfo = { ...stockInfo };
        const updatedCartItemsCount = { ...cartItemsCount };
        
        // Her renk için stok kontrolü
        for (const color of selectedModelColors) {
          const colorId = color.id.toString();
          const key = `${selectedModel}-${colorId}`;
          
          try {
            // API'den stok bilgisini al (kendin tasarla için özel endpoint)
            const stockResponse = await api.get(`/phone-models/${selectedModel}/colors/${colorId}/custom-design-stock?product_type_id=1`);
            const availableStock = stockResponse.data.stock || 0;
            
            // Sepetteki bu model ve renk kombinasyonunu bul - tüm ürünleri kontrol et
            const cartItemsWithSameModelAndColor = cart.filter(item => 
              item.phone_model_id === parseInt(selectedModel) && 
              item.color_id === parseInt(colorId)
            );
            
            // Sepetteki toplam miktarı hesapla
            const cartQuantity = cartItemsWithSameModelAndColor.reduce((sum, item) => sum + item.quantity, 0);
            
            // Kalan stok miktarını hesapla
            const remainingStock = Math.max(0, availableStock - cartQuantity);
            
            updatedStockInfo[key] = {
              colorId,
              modelId: selectedModel,
              available: remainingStock > 0,
              stock: availableStock,
              remainingStock: remainingStock
            };
            
            updatedCartItemsCount[key] = cartQuantity;
            
            // Eğer şu an seçili renk stokta yoksa, toast ile bilgilendir
            if (selectedColor === colorId && remainingStock <= 0) {
              toast.warning(`${color.name} rengi için yeterli stok bulunmamaktadır. (Sepetteki miktar: ${cartQuantity}, Mevcut stok: ${availableStock})`);
            }
          } catch (error) {
            console.error(`Renk ${colorId} için stok kontrolü hatası:`, error);
            updatedStockInfo[key] = {
              colorId,
              modelId: selectedModel,
              available: false,
              stock: 0,
              remainingStock: 0
            };
            updatedCartItemsCount[key] = 0;
          }
        }
        
        setStockInfo(updatedStockInfo);
        setCartItemsCount(updatedCartItemsCount);
        
        // Eğer seçili renk stokta yoksa, kullanıcıya bildir ve seçimi kaldır
        if (selectedColor) {
          const selectedColorKey = `${selectedModel}-${selectedColor}`;
          if (updatedStockInfo[selectedColorKey] && !updatedStockInfo[selectedColorKey].available) {
            // Seçili rengi kaldır
            setCurrentColorStockInfo({
              ...updatedStockInfo[selectedColorKey],
              colorId: selectedColor,
              available: false
            });
          }
        }
      };
      
      checkStockForAllColors();
    }
  }, [selectedModel, selectedModelColors, cart]);

  // LocalStorage'dan AI görseli olup olmadığını kontrol et
  useEffect(() => {
    const aiGeneratedImage = localStorage.getItem('aiGeneratedImage');
    if (aiGeneratedImage) {
      // AI görselini canvas'a ekle
      handleAIGeneratedImage(aiGeneratedImage);
      // LocalStorage'dan temizle
      localStorage.removeItem('aiGeneratedImage');
    }
  }, []);

  const handleAIGeneratedImage = (imageUrl) => {
    fabric.Image.fromURL(
      imageUrl,
      (img) => {
        // Resmi optimize et
        img.scaleToWidth(canvas.width * 0.7);
        
        // Resim canvas'dan büyük olmamalı
        if (img.getScaledHeight() > canvas.height * 0.7) {
          img.scaleToHeight(canvas.height * 0.7);
        }
        
        // Canvas'ın ortasına yerleştir
        img.set({
          left: canvas.width / 2 - img.getScaledWidth() / 2,
          top: canvas.height / 2 - img.getScaledHeight() / 2,
          cornerColor: '#8B4513',
          cornerStrokeColor: '#8B4513',
          borderColor: '#8B4513',
          cornerSize: 10,
          transparentCorners: false,
          objectCaching: true,
        });
        
        // Resmi canvas'a ekle
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        
        // İşlem geçmişine ekle
        saveToHistory();
        
        // Katmanları güncelle
        updateLayers();
        
        // Başarı mesajı göster
        toast.success('Yapay zeka görseli başarıyla eklendi!', {
          position: "bottom-right",
          autoClose: 3000
        });
      },
      { crossOrigin: 'anonymous' }
    );
  };

  // AI görselini direkt olarak eklemek için fonksiyon
  const handleAIImageSelect = (imageUrl) => {
    if (!imageUrl || !canvas) return;
    
    fabric.Image.fromURL(
      imageUrl,
      (img) => {
        // Resmi optimize et
        img.scaleToWidth(canvas.width * 0.7);
        
        // Resim canvas'dan büyük olmamalı
        if (img.getScaledHeight() > canvas.height * 0.7) {
          img.scaleToHeight(canvas.height * 0.7);
        }
        
        // Canvas'ın ortasına yerleştir
        img.set({
          left: canvas.width / 2 - img.getScaledWidth() / 2,
          top: canvas.height / 2 - img.getScaledHeight() / 2,
          cornerColor: '#8B4513',
          cornerStrokeColor: '#8B4513',
          borderColor: '#8B4513',
          cornerSize: 10,
          transparentCorners: false,
          objectCaching: true,
        });
        
        // Resmi canvas'a ekle
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        
        // İşlem geçmişine ekle
        saveToHistory();
        
        // Katmanları güncelle
        updateLayers();
        
        // Başarı mesajı göster
        toast.success('Yapay zeka görseli başarıyla eklendi!', {
          position: "bottom-right",
          autoClose: 3000
        });
        
        // AI panelini kapat
        setShowAIGenerator(false);
      },
      { crossOrigin: 'anonymous' }
    );
  };

  return (
    <div className="custom-design-page position-relative overflow-hidden">
      {/* Paralaks Arka Plan */}
      <div className="position-absolute w-100 h-100" style={{ zIndex: 0 }}>
        {/* Arka plan için beyaz overlay */}
        <div className="position-absolute w-100 h-100 bg-white" style={{ opacity: 0.9, zIndex: 0 }}></div>
        {/* Gradient overlay */}
        <div className="position-absolute w-100 h-100" style={{ 
          background: 'linear-gradient(to bottom, rgba(255,248,230,0.8), rgba(255,255,255,0.9))', 
          zIndex: 1 
        }}></div>
      </div>
      
      {/* Dekoratif Şekiller */}
      <div className="position-absolute rounded-circle" 
        style={{ 
          top: '10%', 
          left: '5%', 
          width: '180px', 
          height: '180px', 
          backgroundColor: '#fcd34d', 
          opacity: 0.2,
          animation: 'blob 7s infinite',
          zIndex: 2
        }}>
      </div>
      <div className="position-absolute rounded-circle" 
        style={{ 
          bottom: '15%', 
          right: '5%', 
          width: '200px', 
          height: '200px', 
          backgroundColor: '#f59e0b', 
          opacity: 0.1,
          animation: 'blob 7s infinite 2s',
          zIndex: 2
        }}>
      </div>
      <div className="position-absolute rounded-circle" 
        style={{ 
          top: '40%', 
          right: '15%', 
          width: '150px', 
          height: '150px', 
          backgroundColor: '#8B4513', 
          opacity: 0.1,
          animation: 'blob 7s infinite 4s',
          zIndex: 2
        }}>
      </div>
      
      <Container fluid className="custom-design-container position-relative" style={{ zIndex: 10 }}>
        <Row className="mobile-first-layout">
          {/* Canvas Alanı - Her zaman üstte */}
          <Col xs={12} md={9} className="canvas-area order-1">
            <canvas id="design-canvas" ref={canvasRef} />
          </Col>

          {/* Araçlar Paneli */}
          <Col xs={12} md={3} className="tools-panel order-2">
             {/* Telefon Modeli Seçimi */}
             <div className="tool-section model-select">
              <h5>Telefon Modeli</h5>
              <Form.Select 
                value={selectedModel} 
                onChange={handleModelSelect}
                disabled={isLoading}
              >
                <option value="">Telefon Modeli Seçin</option>
                {phoneModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.brand} {model.model_name}
                  </option>
                ))}
              </Form.Select>
              
              {/* Renk seçimi */}
              {selectedModelColors.length > 0 && (
                <div className="color-selection">
                  <div className="colors-heading">
                    <h5>Çerçeve Rengi</h5>
                    <FontAwesomeIcon 
                      icon={faInfoCircle} 
                      className="color-info-icon" 
                      onClick={handleColorInfoClick}
                    />
                  </div>
                  <div className="color-options">
                    {selectedModelColors.map((color) => {
                      // Renk için stok bilgisini bul
                      const colorKey = `${selectedModel}-${color.id}`;
                      const colorStockInfo = stockInfo[colorKey];
                      const cartCount = cartItemsCount[colorKey] || 0;
                      const isOutOfStock = colorStockInfo && !colorStockInfo.available;
                      
                      return (
                        <div
                          key={color.id}
                          className={`color-option ${color.code === 'transparent' ? 'transparent' : ''} ${color.code === '#ffffff' || color.code === '#fff' ? 'white' : ''} ${selectedColor === color.id.toString() ? 'selected' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}
                          style={{ backgroundColor: color.code }}
                          onClick={() => !isOutOfStock && handleColorSelect(color.id, color.name)}
                          title={`${color.name}${isOutOfStock ? ` (Stokta Yok - Sepetteki Miktar: ${cartCount}, Toplam Stok: ${colorStockInfo?.stock || 0})` : ''}`}
                        >
                          {isOutOfStock && (
                            <div className="stock-badge">
                              <FontAwesomeIcon icon={faTimes} size="xs" />
                            </div>
                          )}
                          {selectedColor === color.id.toString() && (
                            <FontAwesomeIcon
                              icon={faCheck}
                              className={`color-check ${getColorType(color.code) === 'dark' ? 'light' : 'dark'}`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {selectedColor && selectedModelColors.length > 0 && (
                    <div className="color-name mt-2">
                      {selectedModelColors.find(c => c.id.toString() === selectedColor)?.name || 'Renk seçilmedi'}
                      {currentColorStockInfo && !currentColorStockInfo.available && (
                        <span className="stock-unavailable-text ms-2">
                          (Stokta Yok - Sepetteki Miktar: {currentColorStockInfo.cartQuantity || 0}, 
                          Toplam Stok: {currentColorStockInfo.stock || 0})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Katmanlar Paneli - En üstte */}
            <LayersPanel />

            {/* Temel Araçlar */}
            <div className="tool-section">
              <h5>Araçlar</h5>
              <div className="tool-buttons d-flex flex-wrap gap-2">
                <label className="tool-button-file btn btn-primary flex-grow-1" title="Fotoğraf Yükle">
                  <FontAwesomeIcon icon={faImage} />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <span className="tool-label">Fotoğraf Yükle</span>
                </label>
                <button
                  type="button"
                  title="Fotoğraf Filtreleri"
                  className="tool-button photo-filters-button btn btn-primary flex-grow-1 disabled"
                  disabled
                  aria-disabled="true"
                >
                  <FontAwesomeIcon icon={faFilter} />
                  <span className="new-badge">Yapım aşamasında</span>
                  <span className="tool-label">Fotoğraf Filtreleri</span>
                </button>
                {/* AI görsel oluşturma butonu - Desktop için */}
                <button
                  type="button"
                  title="Yapay Zeka ile Görsel Oluştur"
                  className="tool-button btn btn-primary d-none d-md-flex flex-grow-1"
                  onClick={() => setShowAIGenerator(!showAIGenerator)}
                >
                  <FontAwesomeIcon icon={faMagic} />
                  <span className="tool-label">AI ile Oluştur</span>
                </button>
                {/* Mobil cihazlarda AI görsel butonu - araçlar arasında */}
                <button
                  type="button"
                  title="Yapay Zeka ile Görsel Oluştur"
                  className="tool-button-ai btn btn-primary d-md-none flex-grow-1"
                  onClick={() => setShowAIGenerator(!showAIGenerator)}
                >
                  <FontAwesomeIcon icon={faMagic} className="ai-icon" />
                  <span className="ai-button-text">AI ile Oluştur</span>
                </button>
                <button
                  type="button"
                  title="Hazır Tasarımlar"
                  className="tool-button btn btn-primary flex-grow-1"
                  onClick={() => setShowImageLibrary(!showImageLibrary)}
                >
                  <FontAwesomeIcon icon={faImages} />
                  <span className="tool-label">Hazır Tasarımlar</span>
                </button>
                <button
                  type="button"
                  title="Seçili Öğeyi Sil"
                  className="tool-button btn btn-primary flex-grow-1"
                  onClick={() => {
                    const activeObj = canvas.getActiveObject();
                    if (activeObj) {
                      canvas.remove(activeObj);
                      saveToHistory();
                      updateLayers();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  <span className="tool-label">Sil</span>
                </button>
              </div>
            </div>

            {/* Düzenleme Araçları */}
            {selectedObject && (
              <div className="tool-section edit-tools-section active">
                <div className="section-header">
                  <FontAwesomeIcon icon={faEdit} className="icon" />
                  <h5 className="mb-0">Düzenle</h5>
                </div>
                
                {/* Temel Düzenleme Araçları */}
                <Accordion defaultActiveKey="0" className="mb-3">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Temel Düzenleme</Accordion.Header>
                    <Accordion.Body>
                      <ButtonGroup className="mb-3 w-100">
                        <Button variant="outline-secondary" onClick={flipHorizontal}>
                          <FontAwesomeIcon icon={faArrowsAltH} className="me-2" />
                          Yatay Çevir
                        </Button>
                        <Button variant="outline-secondary" onClick={flipVertical}>
                          <FontAwesomeIcon icon={faArrowsAltV} className="me-2" />
                          Dikey Çevir
                        </Button>
                      </ButtonGroup>
                      
                      <ButtonGroup className="w-100">
                        <Button variant="outline-secondary" onClick={() => rotate(-90)}>
                          <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
                          Sola Döndür
                        </Button>
                        <Button variant="outline-secondary" onClick={() => rotate(90)}>
                          <FontAwesomeIcon icon={faRotateRight} className="me-2" />
                          Sağa Döndür
                        </Button>
                      </ButtonGroup>
                    </Accordion.Body>
                  </Accordion.Item>

                  {selectedObject && selectedObject.type === 'image' && (
                    <>
                      <Accordion.Item eventKey="1">
                        <Accordion.Header>Filtreler</Accordion.Header>
                        <Accordion.Body>
                          <div className="d-grid gap-2">
                            <Button variant="outline-secondary" onClick={() => applyFilter('none')}>Filtreleri Temizle</Button>
                            <Button variant="outline-secondary" onClick={() => applyFilter('grayscale')}>Siyah Beyaz</Button>
                            <Button variant="outline-secondary" onClick={() => applyFilter('sepia')}>Sepya</Button>
                            <Button variant="outline-secondary" onClick={() => applyFilter('invert')}>Negatif</Button>
                            <Button variant="outline-secondary" onClick={() => applyFilter('vintage')}>Vintage</Button>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>

                      <Accordion.Item eventKey="2">
                        <Accordion.Header>Parlaklık ve Kontrast</Accordion.Header>
                        <Accordion.Body>
                          <Form.Group className="mb-3">
                            <Form.Label>Parlaklık</Form.Label>
                            <Form.Range
                              min="-100"
                              max="100"
                              value={selectedObject?.id ? (objectFilters[selectedObject.id]?.brightness || 0) : 0}
                              onChange={(e) => updateImageAdjustments('brightness', parseInt(e.target.value))}
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Kontrast</Form.Label>
                            <Form.Range
                              min="-100"
                              max="100"
                              value={selectedObject?.id ? (objectFilters[selectedObject.id]?.contrast || 0) : 0}
                              onChange={(e) => updateImageAdjustments('contrast', parseInt(e.target.value))}
                            />
                          </Form.Group>
                        </Accordion.Body>
                      </Accordion.Item>

                      <Accordion.Item eventKey="3">
                        <Accordion.Header>Renk Ayarları</Accordion.Header>
                        <Accordion.Body>
                          <Form.Group className="mb-3">
                            <Form.Label>Doygunluk</Form.Label>
                            <Form.Range
                              min="-100"
                              max="100"
                              value={selectedObject?.id ? (objectFilters[selectedObject.id]?.saturation || 0) : 0}
                              onChange={(e) => updateImageAdjustments('saturation', parseInt(e.target.value))}
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Renk Tonu</Form.Label>
                            <Form.Range
                              min="-180"
                              max="180"
                              value={selectedObject?.id ? (objectFilters[selectedObject.id]?.hue || 0) : 0}
                              onChange={(e) => updateImageAdjustments('hue', parseInt(e.target.value))}
                            />
                          </Form.Group>
                        </Accordion.Body>
                      </Accordion.Item>

                      <Accordion.Item eventKey="4">
                        <Accordion.Header>Detay Ayarları</Accordion.Header>
                        <Accordion.Body>
                          <Form.Group className="mb-3">
                            <Form.Label>Bulanıklık</Form.Label>
                            <Form.Range
                              min="0"
                              max="100"
                              value={selectedObject?.id ? (objectFilters[selectedObject.id]?.blur || 0) : 0}
                              onChange={(e) => updateImageAdjustments('blur', parseInt(e.target.value))}
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Keskinlik</Form.Label>
                            <Form.Range
                              min="0"
                              max="100"
                              value={selectedObject?.id ? (objectFilters[selectedObject.id]?.sharpen || 0) : 0}
                              onChange={(e) => updateImageAdjustments('sharpen', parseInt(e.target.value))}
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Gama</Form.Label>
                            <Form.Range
                              min="0.1"
                              max="2.2"
                              step="0.1"
                              value={selectedObject?.id ? (objectFilters[selectedObject.id]?.gamma || 1) : 1}
                              onChange={(e) => updateImageAdjustments('gamma', parseFloat(e.target.value))}
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Pikselleştirme</Form.Label>
                            <Form.Range
                              min="1"
                              max="20"
                              value={selectedObject?.id ? (objectFilters[selectedObject.id]?.pixelate || 1) : 1}
                              onChange={(e) => updateImageAdjustments('pixelate', parseInt(e.target.value))}
                            />
                          </Form.Group>
                        </Accordion.Body>
                      </Accordion.Item>
                    </>
                  )}
                </Accordion>
              </div>
            )}

            {/* Geri/İleri Butonları */}
            <div className="history-controls">
              <Button onClick={undo} disabled={historyIndex <= 0} title="Geri Al">
                <FontAwesomeIcon icon={faUndo} />
              </Button>
              <Button onClick={redo} disabled={historyIndex >= history.length - 1} title="İleri Al">
                <FontAwesomeIcon icon={faRedo} />
              </Button>
            </div>

            {/* Sepete Ekle Butonu */}
            <div className="action-buttons d-flex flex-column justify-content-center align-items-center w-100">
              {/* Add the approval checkbox before the add to cart button */}
              <div className="approval-section w-100">
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="approval-checkbox"
                    className="form-check-input"
                    checked={isApprovalChecked}
                    onChange={handleApprovalChange}
                  />
                  <label title="" htmlFor="approval-checkbox" className="form-check-label">
                    <span className="approval-label">
                      Tasarımımın web sitemizde yayınlanmasını istiyorum.
                      <button
                        type="button"
                        className="details-link btn btn-link"
                        onClick={handleApprovalInfoClick}
                      >
                        Detaylar için tıklayın
                      </button>
                    </span>
                  </label>
                </div>
              </div>

              {/* Add the smaller price info directly above the Add to Cart button */}
              <div className="price-info-small mb-2 w-100">
                <span className="price">{kendinTasarlaFee !== null ? kendinTasarlaFee.toFixed(2) : 'Yükleniyor...'} TL</span>
                <span className="price-description">Özel tasarım kılıf</span>
              </div>

              <Button
                type="button"
                className="add-to-cart-btn w-100" 
                onClick={addDesignToCart}
                disabled={
                  isLoading || 
                  !selectedModel || 
                  !selectedColor || 
                  !canvas || 
                  (currentColorStockInfo && !currentColorStockInfo.available)
                }
              >
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
                    Sepete Ekle
                    {currentColorStockInfo && !currentColorStockInfo.available && " (Stok Yok)"}
                  </>
                )}
              </Button>
            </div>

            {/* Görsel Kütüphanesi - Modal olarak göster */}
            <Modal
              show={showImageLibrary}
              onHide={() => setShowImageLibrary(false)}
              centered
              size="lg"
              className="design-library-modal"
            >
              <Modal.Header closeButton>
                <Modal.Title>Hazır Tasarım Öğeleri</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="image-grid">
                  {isLoadingDesigns ? (
                    <div className="loading-designs">Tasarımlar yükleniyor...</div>
                  ) : designs.length > 0 ? (
                    designs.map((design) => (
                      <div
                        key={design.id}
                        className="library-image"
                        onClick={() => {
                          addLibraryImage(design.image_url);
                          setShowImageLibrary(false);
                        }}
                      >
                        <div className="library-image-wrapper">
                          <img src={design.image_url} alt={design.name} />
                        </div>
                        <span className="library-image-name">{design.name}</span>
                      </div>
                    ))
                  ) : (
                    <div className="no-designs">Henüz tasarım eklenmemiş</div>
                  )}
                </div>
              </Modal.Body>
            </Modal>

            {/* Color Info Modal */}
            <Modal 
              show={showColorInfo} 
              onHide={handleCloseColorInfo} 
              centered
              className="color-info-modal"
            >
              <Modal.Header closeButton>
                <Modal.Title>Renk Seçenekleri Hakkında</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Değerli Müşterilerimiz,</p>
                <p>Sublimasyon baskı ile üretilen sert plastik telefon kılıflarımız, yalnızca beyaz, siyah ve transparan yan kenar renklerine sahiptir.</p>
                <ul>
                  <li><strong>Beyaz Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları beyazdır.</li>
                  <li><strong>Siyah Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları siyahtır.</li>
                  <li><strong>Transparan Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları şeffaftır ve telefonunuzun rengini gösterir.</li>
                </ul>
                <p>Baskı işlemi yalnızca kılıfın arka yüzeyine uygulanmaktadır. Yan kenarlara herhangi bir baskı yapılmamaktadır. Sipariş verirken bu bilgiyi göz önünde bulundurmanızı rica ederiz.</p>
                <p>Anlayışınız için teşekkür eder, keyifli alışverişler dileriz!</p>
                <p><strong>Mr Brown Case</strong></p>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseColorInfo}>
                  Kapat
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Approval Info Modal */}
            <Modal 
              show={showApprovalInfo} 
              onHide={handleCloseApprovalInfo} 
              centered
              className="approval-info-modal"
            >
              <Modal.Header closeButton>
                <Modal.Title>Tasarım Yayınlama Hakkında</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Değerli Müşterilerimiz,</p>
                <p>"Kendin Tasarla" özelliği ile tamamen size özel telefon kılıfı tasarımları oluşturabilirsiniz. Tasarımınızı yükledikten sonra, ekibimiz tarafından incelenerek onay sürecinden geçirilecektir.</p>
                <p>Eğer tasarımınız bizim içerik ve kalite kriterlerimize uygunsa ve siz de onay verirseniz, tasarımınız diğer müşterilerimizin de satın alabileceği şekilde sitemizde yayınlanabilir.</p>
                <p>Telif hakkı ihlali, müstehcen veya yasa dışı içerik içeren tasarımlar onaylanmayacaktır.</p>
                <p>Kendi tarzınızı yansıtmanın keyfini çıkarın!</p>
                <p><strong>Mr Brown Case</strong></p>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseApprovalInfo}>
                  Kapat
                </Button>
              </Modal.Footer>
            </Modal>

            {/* AI Görsel Oluşturma paneli */}
            {showAIGenerator && (
              <>
                <div className="ai-overlay" onClick={() => setShowAIGenerator(false)}></div>
                <div className="ai-generator-panel tool-section">
                  <button 
                    type="button" 
                    className="modal-close-btn" 
                    onClick={() => setShowAIGenerator(false)}
                    aria-label="Kapat"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                  <div className="ai-panel-content">
                    <AIImageGenerator onImageSelect={handleAIImageSelect} />
                  </div>
                </div>
              </>
            )}

            {/* Fotoğraf Filtreleri - Modal olarak göster */}
            <Modal
              show={showPhotoFilters}
              onHide={() => setShowPhotoFilters(false)}
              centered
              size="lg"
              className="photo-filters-modal"
            >
              <Modal.Header closeButton>
                <Modal.Title>Yapay Zeka Fotoğraf Filtreleri</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <PhotoFilters onProcessingComplete={handleFilteredImageReady} />
              </Modal.Body>
            </Modal>
          </Col>
        </Row>
      </Container>
      
      {/* Animasyon için CSS */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
      `}</style>
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-content2">
            <div className="spinner-border text-light mb-3" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </div>
            <h5 className="loading-text">Tasarımınız Yükleniyor</h5>
            <p className="loading-description">{loadingStatus || "Lütfen bekleyin..."}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDesign; 